import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { brandingAPI } from '../services/api';
import { 
  LayoutDashboard, Users, Briefcase, FileText, ShoppingCart, 
  DollarSign, Calendar, Mail, BarChart3, Settings, LogOut,
  Menu, X, ChevronDown, FolderOpen, Receipt
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const navigation = [
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard, roles: ['admin', 'staff', 'trade', 'superuser'] },
  { name: 'Jobs', href: '/jobs', icon: Briefcase, roles: ['admin', 'staff', 'trade', 'superuser'] },
  { name: 'Contractors', href: '/contractors', icon: Users, roles: ['admin', 'staff', 'superuser'] },
  { name: 'Quotes', href: '/quotes', icon: FileText, roles: ['admin', 'staff', 'superuser'] },
  { name: 'Purchase Orders', href: '/purchase-orders', icon: ShoppingCart, roles: ['admin', 'staff', 'superuser'] },
  { name: 'Schedule of Rates', href: '/pricing', icon: DollarSign, roles: ['admin', 'staff', 'superuser'] },
  { name: 'Invoices', href: '/invoices', icon: FileText, roles: ['admin', 'staff', 'superuser'] },
  { name: 'Invoicing', href: '/invoicing', icon: Receipt, roles: ['admin', 'staff', 'superuser'] },
  { name: 'Documents', href: '/documents', icon: FolderOpen, roles: ['admin', 'staff', 'superuser'] },
  { name: 'Schedule', href: '/schedule', icon: Calendar, roles: ['admin', 'staff', 'trade', 'superuser'] },
  { name: 'Emails', href: '/emails', icon: Mail, roles: ['admin', 'staff', 'superuser'] },
  { name: 'Reports', href: '/reports', icon: BarChart3, roles: ['admin', 'staff', 'superuser'] },
  { name: 'Settings', href: '/settings', icon: Settings, roles: ['admin', 'superuser'] },
];

// Default branding (Formiq Building as the example customer)
const defaultBranding = {
  company_name: 'Formiq Building',
  logo_url: 'https://customer-assets.emergentagent.com/job_jobflow-portal/artifacts/50f8u3xl_ChatGPT%20Image%20Jan%202%2C%202026%2C%2010_02_05%20AMnew.jpg',
};

// ConstructOne platform logo (for sidebar)
const platformLogo = 'https://customer-assets.emergentagent.com/job_ezclaimwin/artifacts/aasxwc8a_ChatGPT_Image_Jan_4__2026__09_48_44_AM-removebg-preview.png';

export const MainLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [branding, setBranding] = useState(defaultBranding);

  useEffect(() => {
    // Fetch company branding on mount
    const fetchBranding = async () => {
      try {
        const response = await brandingAPI.get();
        if (response.data) {
          setBranding({
            ...defaultBranding,
            ...response.data
          });
        }
      } catch (error) {
        console.log('Using default branding');
      }
    };
    fetchBranding();
  }, []);

  const filteredNav = navigation.filter(item => item.roles.includes(user?.role || 'staff'));

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="h-screen overflow-hidden bg-background bg-grid">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`fixed top-0 left-0 z-50 h-full w-64 bg-card border-r border-border transform transition-transform duration-200 lg:translate-x-0 flex flex-col ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        {/* ConstructOne Platform Logo in Sidebar */}
        <div className="flex items-center justify-center h-20 px-4 border-b border-border bg-background/50">
          <img 
            src={platformLogo} 
            alt="ConstructOne" 
            className="h-16 w-auto object-contain"
          />
        </div>
        
        <nav className="p-4 space-y-1 flex-1">
          {filteredNav.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.href;
            return (
              <Link
                key={item.name}
                to={item.href}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-3 py-2 rounded-sm text-sm font-medium transition-colors ${
                  isActive 
                    ? 'bg-primary text-primary-foreground' 
                    : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
                }`}
                data-testid={`nav-${item.name.toLowerCase().replace(' ', '-')}`}
              >
                <Icon className="h-5 w-5" />
                {item.name}
              </Link>
            );
          })}
        </nav>
        
        {/* Powered by ConstructOne (Platform branding) */}
        <div className="p-4 border-t border-border bg-background/30">
          <p className="text-xs text-muted-foreground text-center mb-2">Powered by</p>
          <img 
            src={platformLogo}
            alt="ConstructOne" 
            className="h-8 object-contain mx-auto"
          />
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64 h-screen flex flex-col">
        {/* Top navbar */}
        <header className="sticky top-0 z-30 h-24 bg-card/80 backdrop-blur-md border-b border-border flex-shrink-0">
          <div className="flex items-center justify-between h-full px-4 lg:px-6">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 text-muted-foreground hover:text-foreground"
              data-testid="mobile-menu-btn"
            >
              <Menu className="h-6 w-6" />
            </button>

            {/* Header - Customer Branding (centered) */}
            <div className="flex-1 flex justify-center">
              {branding.logo_url ? (
                <img 
                  src={branding.logo_url}
                  alt={branding.company_name} 
                  className="h-20 object-contain"
                />
              ) : (
                <h1 className="text-2xl font-bold text-primary">{branding.company_name}</h1>
              )}
            </div>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-btn">
                  <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                    <span className="text-sm font-bold text-primary">
                      {user?.name?.charAt(0).toUpperCase() || 'U'}
                    </span>
                  </div>
                  <span className="hidden sm:block text-sm font-medium">{user?.name}</span>
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-card border-border">
                <div className="px-3 py-2">
                  <p className="text-sm font-medium">{user?.name}</p>
                  <p className="text-xs text-muted-foreground">{user?.email}</p>
                  <p className="text-xs text-primary uppercase mt-1">{user?.role}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-destructive cursor-pointer" data-testid="logout-btn">
                  <LogOut className="h-4 w-4 mr-2" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-auto p-4 lg:p-6">
          {children}
        </main>
      </div>
    </div>
  );
};

export default MainLayout;
